import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  getDocs, 
  where,
  updateDoc,
  increment,
  doc,
  writeBatch
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import './GalleryPage.css';

// Optimized Image component with skeleton loading and fade-in
const OptimizedImage = ({ src, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    // Reset state when src changes
    setIsLoaded(false);
    setHasError(false);

    // Check if image is already cached in browser
    if (imgRef.current?.complete && imgRef.current?.naturalHeight !== 0) {
      setIsLoaded(true);
    }
  }, [src]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoaded(true); // Show error state instead of skeleton
  }, []);

  return (
    <div className={`optimized-image-container ${className || ''}`}>
      {/* Skeleton placeholder - visible while loading */}
      <div className={`image-skeleton ${isLoaded ? 'hidden' : ''}`}>
        <div className="skeleton-shimmer" />
      </div>
      
      {/* Actual image - starts invisible, fades in when loaded */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`optimized-image ${isLoaded ? 'loaded' : ''} ${hasError ? 'error' : ''}`}
        onLoad={handleLoad}
        onError={handleError}
        decoding="async"
      />
      
      {/* Error fallback */}
      {hasError && (
        <div className="image-error">
          <span>Failed to load</span>
        </div>
      )}
    </div>
  );
};

// Custom Dropdown Component
const CustomDropdown = ({ options, value, onChange, placeholder, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const selectedOption = options.find(opt => String(opt.value) === String(value)) || options[0];

  return (
    <div className={`custom-dropdown ${className}`} ref={dropdownRef}>
      <button 
        className="custom-dropdown-button"
        onClick={handleToggle}
        type="button"
      >
        {selectedOption.label}
      </button>
      {isOpen && (
        <div className="custom-dropdown-list">
          {options.map((option) => (
            <button
              key={option.value}
              className={`custom-dropdown-option ${String(option.value) === String(value) ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const GalleryPage = () => {
  const { currentUser } = useAuth();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userVotes, setUserVotes] = useState(new Set());
  
  // Load settings from localStorage or use defaults
  const [sortBy, setSortBy] = useState(() => {
    const saved = localStorage.getItem('gallery-sortBy');
    return saved || 'votes';
  });
  const [filterRegion, setFilterRegion] = useState(() => {
    const saved = localStorage.getItem('gallery-filterRegion');
    return saved || 'all';
  });
  const [filterYear, setFilterYear] = useState(() => {
    const saved = localStorage.getItem('gallery-filterYear');
    return saved || 'all';
  });
  const [filterMonth, setFilterMonth] = useState(() => {
    const saved = localStorage.getItem('gallery-filterMonth');
    return saved || 'all';
  });
  
  const [availableStates, setAvailableStates] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Track previous filter values so we only reset pagination when filters change (not on vote count updates)
  const prevFiltersRef = useRef({ sortBy, filterRegion, filterYear, filterMonth });
  
  // Caching state
  const [allDesigns, setAllDesigns] = useState([]); // Master list of all designs
  const [filteredDesigns, setFilteredDesigns] = useState([]); // Currently displayed designs
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const designsPerPage = 12;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const normalizeFilterValue = (value) => (value === 'all' ? 'all' : String(value));

  // Reset persisted filters that no longer match available options (stale localStorage)
  useEffect(() => {
    if (allDesigns.length === 0) return;

    const resetIfStale = (key, current, available, isValid) => {
      if (current === 'all' || available.length === 0 || isValid(current)) return;
      localStorage.removeItem(key);
      return 'all';
    };

    const nextRegion = resetIfStale(
      'gallery-filterRegion',
      filterRegion,
      availableStates,
      (v) => availableStates.includes(v)
    );
    const nextYear = resetIfStale(
      'gallery-filterYear',
      filterYear,
      availableYears,
      (v) => availableYears.includes(Number(v))
    );
    const nextMonth = resetIfStale(
      'gallery-filterMonth',
      filterMonth,
      availableMonths,
      (v) => availableMonths.includes(Number(v))
    );

    if (nextRegion) setFilterRegion(nextRegion);
    if (nextYear) setFilterYear(nextYear);
    if (nextMonth) setFilterMonth(nextMonth);
  }, [allDesigns.length, availableStates, availableYears, availableMonths, filterRegion, filterYear, filterMonth]);

  // Helper functions to save settings to localStorage
  const saveSortBy = (value) => {
    setSortBy(value);
    localStorage.setItem('gallery-sortBy', value);
  };

  const saveFilterRegion = (value) => {
    const normalized = normalizeFilterValue(value);
    setFilterRegion(normalized);
    localStorage.setItem('gallery-filterRegion', normalized);
  };

  const saveFilterYear = (value) => {
    const normalized = normalizeFilterValue(value);
    setFilterYear(normalized);
    localStorage.setItem('gallery-filterYear', normalized);
  };

  const saveFilterMonth = (value) => {
    const normalized = normalizeFilterValue(value);
    setFilterMonth(normalized);
    localStorage.setItem('gallery-filterMonth', normalized);
  };

  // Load all designs once on initial load
  useEffect(() => {
    if (!hasInitialLoad) {
      fetchAllDesigns();
      setHasInitialLoad(true);
    }
  }, [hasInitialLoad]);

  // Apply filters to cached data instead of refetching
  useEffect(() => {
    if (allDesigns.length > 0) {
      applyFilters();
    }
  }, [sortBy, filterRegion, filterYear, filterMonth, allDesigns]);

  // Fetch user votes and available filter options
  useEffect(() => {
    if (currentUser) {
      fetchUserVotes();
    } else {
      setUserVotes(new Set());
    }
    fetchAvailableStates();
    fetchAvailableYears();
    fetchAvailableMonths();
  }, [currentUser]);

  const fetchAllDesigns = async () => {
    try {
      setLoading(true);
      const designsRef = collection(db, 'designs');
      const querySnapshot = await getDocs(designsRef);
      
      const designs = [];
      querySnapshot.forEach((doc) => {
        designs.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setAllDesigns(designs);
    } catch (error) {
      console.error("Error fetching all designs: ", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...allDesigns];
    
    // Apply region filter
    if (filterRegion !== 'all') {
      filtered = filtered.filter(design => design.state === filterRegion);
    }
    
    // Apply date filters
    if (filterYear !== 'all' || filterMonth !== 'all') {
      filtered = filtered.filter(design => {
        if (!design.createdAt) return false;
        
        const designDate = design.createdAt.toDate();
        const designYear = designDate.getFullYear();
        const designMonth = designDate.getMonth() + 1;
        
        if (filterYear !== 'all' && designYear !== parseInt(filterYear)) {
          return false;
        }
        
        if (filterMonth !== 'all' && designMonth !== parseInt(filterMonth)) {
          return false;
        }
        
        return true;
      });
    }
    
    // Apply sorting
    if (sortBy === 'votes') {
      filtered.sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0));
    } else if (sortBy === 'recent') {
      filtered.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
    }
    
    // Only reset pagination when sort/filter values actually changed (not on vote count updates)
    const prev = prevFiltersRef.current;
    const filtersChanged = prev.sortBy !== sortBy || prev.filterRegion !== filterRegion ||
      prev.filterYear !== filterYear || prev.filterMonth !== filterMonth;
    if (filtersChanged) {
      setCurrentPage(1);
      prevFiltersRef.current = { sortBy, filterRegion, filterYear, filterMonth };
    }
    
    // When filters changed we show page 1; otherwise keep showing up to currentPage (avoids "refresh" on vote)
    const pageToShow = filtersChanged ? 1 : currentPage;
    const paginated = filtered.slice(0, pageToShow * designsPerPage);
    setFilteredDesigns(paginated);
    setHasMore(filtered.length > pageToShow * designsPerPage);
    
    // Update the main designs state for compatibility
    setDesigns(paginated);
  }, [allDesigns, filterRegion, filterYear, filterMonth, sortBy, currentPage]);

  const loadMoreDesigns = () => {
    const allFiltered = getFilteredResults();
    const nextPage = currentPage + 1;
    const startIndex = (nextPage - 1) * designsPerPage;
    const endIndex = startIndex + designsPerPage;
    
    const nextBatch = allFiltered.slice(startIndex, endIndex);
    
    if (nextBatch.length > 0) {
      setFilteredDesigns(prev => [...prev, ...nextBatch]);
      setDesigns(prev => [...prev, ...nextBatch]); // Update main state for compatibility
      setCurrentPage(nextPage);
      setHasMore(endIndex < allFiltered.length);
    }
  };

  const getFilteredResults = () => {
    let filtered = [...allDesigns];
    
    // Apply region filter
    if (filterRegion !== 'all') {
      filtered = filtered.filter(design => design.state === filterRegion);
    }
    
    // Apply date filters
    if (filterYear !== 'all' || filterMonth !== 'all') {
      filtered = filtered.filter(design => {
        if (!design.createdAt) return false;
        
        const designDate = design.createdAt.toDate();
        const designYear = designDate.getFullYear();
        const designMonth = designDate.getMonth() + 1;
        
        if (filterYear !== 'all' && designYear !== parseInt(filterYear)) {
          return false;
        }
        
        if (filterMonth !== 'all' && designMonth !== parseInt(filterMonth)) {
          return false;
        }
        
        return true;
      });
    }
    
    // Apply sorting
    if (sortBy === 'votes') {
      filtered.sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0));
    } else if (sortBy === 'recent') {
      filtered.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
    }
    
    return filtered;
  };

  const fetchAvailableStates = async () => {
    try {
      // Use cached data if available, otherwise fetch from Firestore
      if (allDesigns.length > 0) {
        const states = new Set();
        allDesigns.forEach((design) => {
          if (design.state) {
            states.add(design.state);
          }
        });
        
        // Convert to array and sort alphabetically
        const statesArray = Array.from(states).sort();
        setAvailableStates(statesArray);
      } else {
        // Fallback to Firestore if no cached data
        const designsRef = collection(db, 'designs');
        const querySnapshot = await getDocs(designsRef);
        const states = new Set();
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.state) {
            states.add(data.state);
          }
        });
        
        // Convert to array and sort alphabetically
        const statesArray = Array.from(states).sort();
        setAvailableStates(statesArray);
      }
    } catch (error) {
      console.error("Error fetching available states: ", error);
    }
  };

  const fetchAvailableYears = async () => {
    try {
      // Use cached data if available, otherwise fetch from Firestore
      if (allDesigns.length > 0) {
        const years = new Set();
        allDesigns.forEach((design) => {
          if (design.createdAt) {
            const year = design.createdAt.toDate().getFullYear();
            years.add(year);
          }
        });
        
        // Convert to array and sort descending (newest first)
        const yearsArray = Array.from(years).sort((a, b) => b - a);
        setAvailableYears(yearsArray);
      } else {
        // Fallback to Firestore if no cached data
        const designsRef = collection(db, 'designs');
        const querySnapshot = await getDocs(designsRef);
        const years = new Set();
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.createdAt) {
            const year = data.createdAt.toDate().getFullYear();
            years.add(year);
          }
        });
        
        // Convert to array and sort descending (newest first)
        const yearsArray = Array.from(years).sort((a, b) => b - a);
        setAvailableYears(yearsArray);
      }
    } catch (error) {
      console.error("Error fetching available years: ", error);
    }
  };

  const fetchAvailableMonths = async () => {
    try {
      // Use cached data if available, otherwise fetch from Firestore
      if (allDesigns.length > 0) {
        const months = new Set();
        allDesigns.forEach((design) => {
          if (design.createdAt) {
            const month = design.createdAt.toDate().getMonth() + 1; // getMonth() returns 0-11
            months.add(month);
          }
        });
        
        // Convert to array and sort ascending (January first)
        const monthsArray = Array.from(months).sort((a, b) => a - b);
        setAvailableMonths(monthsArray);
      } else {
        // Fallback to Firestore if no cached data
        const designsRef = collection(db, 'designs');
        const querySnapshot = await getDocs(designsRef);
        const months = new Set();
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.createdAt) {
            const month = data.createdAt.toDate().getMonth() + 1; // getMonth() returns 0-11
            months.add(month);
          }
        });
        
        // Convert to array and sort ascending (January first)
        const monthsArray = Array.from(months).sort((a, b) => a - b);
        setAvailableMonths(monthsArray);
      }
    } catch (error) {
      console.error("Error fetching available months: ", error);
    }
  };

  const fetchUserVotes = async () => {
    try {
      const votesRef = collection(db, 'votes');
      const q = query(votesRef, where('userId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      const userVoteSet = new Set();
      querySnapshot.forEach((doc) => {
        userVoteSet.add(doc.data().designId);
      });
      setUserVotes(userVoteSet);
    } catch (error) {
      console.error("Error fetching user votes: ", error);
    }
  };

  const handleUpvote = async (designId) => {
    if (!currentUser) return;
    
    // Check if user has already voted - if so, do nothing
    if (userVotes.has(designId)) {
      return;
    }
    
    try {
      // Persist vote and increment design count in one batch (so reload shows correct state)
      const batch = writeBatch(db);
      const voteId = `${currentUser.uid}_${designId}`;
      const voteRef = doc(db, 'votes', voteId);
      batch.set(voteRef, { userId: currentUser.uid, designId });
      const designRef = doc(db, 'designs', designId);
      batch.update(designRef, { voteCount: increment(1) });
      await batch.commit();

      // Update local state only after successful persist
      setUserVotes(prev => new Set([...prev, designId]));
      setDesigns(prev => prev.map(design =>
        design.id === designId
          ? { ...design, voteCount: (design.voteCount || 0) + 1 }
          : design
      ));
      setFilteredDesigns(prev => prev.map(design =>
        design.id === designId
          ? { ...design, voteCount: (design.voteCount || 0) + 1 }
          : design
      ));
      setAllDesigns(prev => prev.map(design =>
        design.id === designId
          ? { ...design, voteCount: (design.voteCount || 0) + 1 }
          : design
      ));
    } catch (error) {
      console.error("Error updating vote: ", error);
    }
  };

  const resetAllFilters = () => {
    setFilterRegion('all');
    setFilterYear('all');
    setFilterMonth('all');
    setSortBy('votes');
    setCurrentPage(1);
    
    // Clear localStorage
    localStorage.removeItem('gallery-filterRegion');
    localStorage.removeItem('gallery-filterYear');
    localStorage.removeItem('gallery-filterMonth');
    localStorage.removeItem('gallery-sortBy');
  };

  const DesignCard = ({ design, rank }) => {
    const hasVoted = userVotes.has(design.id);
    const isOwner = currentUser && design.userId === currentUser.uid;
    
    const handleVoteClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleUpvote(design.id);
    };

    const handleDesignerClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Navigate to designer profile
      window.location.href = `/gallery/user/${design.userId}`;
    };

    return (
      <Link to={`/gallery/${design.id}`} className="consistent-design-card">
        <div className="design-image">
          <OptimizedImage src={design.imageUrl} alt={design.name} />
          {rank && <div className="rank-badge">#{rank}</div>}
        </div>
        <div className="design-info">
          <h4 className="design-name">{design.name || 'No Name'}</h4>
          <p className="designer-name">
            by{' '}
            <button
              className="designer-link"
              onClick={handleDesignerClick}
            >
              {design.designerName}
            </button>
          </p>
          {design.state && (
            <p className="design-location">
              {design.county && design.county !== 'N/A' ? `${design.county}, ${design.state}` : design.state}
            </p>
          )}
          <p className="design-date">
            {design.createdAt?.toDate().toLocaleDateString()}
          </p>
          <div className="design-stats">
            <span className="vote-count">❤️ {design.voteCount || 0}</span>
          </div>
          {isOwner ? (
            <button className="vote-btn your-design" disabled>
              Your Design
            </button>
          ) : (
            <button
              className={`vote-btn ${hasVoted ? 'voted' : ''}`}
              onClick={handleVoteClick}
              disabled={hasVoted}
            >
              {hasVoted ? 'Voted' : 'Upvote'}
            </button>
          )}
        </div>
      </Link>
    );
  };

  if (loading) {
    return <div className="gallery-page-loading">Loading gallery...</div>;
  }

  const submittedDesigns = allDesigns.filter((design) => design.status === 'submitted');
  const filteredSubmitted = getFilteredResults().filter((design) => design.status === 'submitted');
  const hasActiveFilters = filterRegion !== 'all' || filterYear !== 'all' || filterMonth !== 'all';
  const galleryCountLabel = hasActiveFilters
    ? `${filteredSubmitted.length} of ${submittedDesigns.length} designs`
    : `${submittedDesigns.length} designs generated`;

  return (
    <div className="gallery-page">
      <div className="page-header">
        <h1>Design Gallery</h1>
        <p className="gallery-design-count">{galleryCountLabel}</p>
        <p>Discover amazing camo patterns from the community</p>
      </div>

      <div className="gallery-controls">
        <div className="sort-controls">
          <button 
            className="sort-toggle"
            onClick={() => saveSortBy(sortBy === 'votes' ? 'recent' : 'votes')}
          >
            {sortBy === 'votes' ? 'Most Popular' : 'Most Recent'}
          </button>
        </div>
        
        <div className="filter-controls">
          <div className="filter-group">
            <CustomDropdown
              options={[
                { label: 'All States', value: 'all' }, 
                ...availableStates.map(state => ({ label: state, value: state })),
                // Ensure saved value is always in options
                ...(filterRegion !== 'all' && !availableStates.includes(filterRegion) ? 
                  [{ label: filterRegion, value: filterRegion }] : [])
              ]}
              value={filterRegion}
              onChange={saveFilterRegion}
              placeholder="All States"
              className="region-filter"
            />
          </div>
          
          <div className="filter-group">
            <CustomDropdown
              options={[
                { label: 'All Years', value: 'all' }, 
                ...availableYears.map(year => ({ label: String(year), value: String(year) })),
                // Ensure saved value is always in options
                ...(filterYear !== 'all' && !availableYears.includes(Number(filterYear)) ?
                  [{ label: filterYear, value: filterYear }] : [])
              ]}
              value={filterYear}
              onChange={saveFilterYear}
              placeholder="All Years"
              className="year-filter"
            />
          </div>
          
          <div className="filter-group">
            <CustomDropdown
              options={[
                { label: 'All Months', value: 'all' }, 
                ...availableMonths.map(month => ({ label: monthNames[month - 1], value: String(month) })),
                // Ensure saved value is always in options
                ...(filterMonth !== 'all' && !availableMonths.includes(Number(filterMonth)) ?
                  [{ label: monthNames[Number(filterMonth) - 1], value: filterMonth }] : [])
              ]}
              value={filterMonth}
              onChange={saveFilterMonth}
              placeholder="All Months"
              className="month-filter"
            />
          </div>
        </div>
      </div>

      <div className="designs-grid">
        {filteredDesigns.map((design, index) => (
          <DesignCard 
            key={design.id} 
            design={design} 
            rank={sortBy === 'votes' ? index + 1 : null}
          />
        ))}
      </div>

      {hasMore && (
        <div className="load-more-container">
          <button 
            className="load-more-btn"
            onClick={loadMoreDesigns}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load More Designs'}
          </button>
        </div>
      )}

      {!loading && filteredDesigns.length === 0 && (
        <div className="no-designs">
          <p>No designs found for the selected criteria.</p>
          <p>Try adjusting your filters or check back later!</p>
          <button className="reset-filters-btn" onClick={resetAllFilters}>
            Reset All Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
